/**
 * ====================================================================
 * FIKO GD&T MANAGER
 * ====================================================================
 *
 * Merkezi GD&T yönetim sistemi. Tüm Feature Control Frame,
 * Datum Feature ve tolerans annotation'larını yönetir.
 *
 * Özellikler:
 * - CRUD operasyonları
 * - Validation (ASME Y14.5-2018)
 * - Serialization/Deserialization
 * - PMI Table entegrasyonu
 * - Event system
 *
 * @author FikoYT CAD Team
 * @version 2.0.0
 */

import * as THREE from 'three';
import { FikoSahne } from '../../IFikoThreeJS/FikoSahne';
import {
    GDTCharacteristicType,
    MaterialCondition,
    ToleranceZoneModifier,
    DatumType,
    DatumReference,
    DatumFeatureInfo,
    FCFComponent,
    FeatureControlFrameConfig,
    FCFStyleConfig,
    PMIData,
    PMIElementType,
    PMITable,
    PMISavedView,
    GDTValidationResult,
    GDTValidationError,
    GDTValidationWarning,
    requiresDatum,
    isDeprecated,
    getCharacteristicCategory
} from './GDTTypes';
import { FeatureControlFrame, createFCF } from './FeatureControlFrame';
import { DatumFeature, DatumTarget, createDatum, createDatumTarget } from './DatumFeature';

// ====================================================================
// EVENT TYPES
// ====================================================================

export type GDTEventType =
    | 'fcf:added'
    | 'fcf:updated'
    | 'fcf:removed'
    | 'datum:added'
    | 'datum:updated'
    | 'datum:removed'
    | 'validation:error'
    | 'pmi:changed';

export interface GDTEvent {
    type: GDTEventType;
    data?: any;
    timestamp: number;
}

type GDTEventListener = (event: GDTEvent) => void;

// ====================================================================
// GDT MANAGER CLASS
// ====================================================================

/**
 * Merkezi GD&T Yönetim Sistemi
 */
export class GDTManager {
    // ═══════════════════════════════════════════════════════════════
    // PROPERTIES
    // ═══════════════════════════════════════════════════════════════

    /** Sahne referansı */
    private sahne: FikoSahne;

    /** Feature Control Frame'ler */
    private fcfMap: Map<string, FeatureControlFrame> = new Map();

    /** Datum Feature'lar */
    private datumMap: Map<string, DatumFeature> = new Map();

    /** Datum Target'lar */
    private datumTargetMap: Map<string, DatumTarget> = new Map();

    /** PMI Table */
    private pmiTable: PMITable;

    /** Event listeners */
    private eventListeners: Map<GDTEventType, Set<GDTEventListener>> = new Map();

    /** Auto-validation */
    private autoValidate: boolean = true;

    /** Varsayılan stil */
    private defaultStyle: Partial<FCFStyleConfig> = {};

    // ═══════════════════════════════════════════════════════════════
    // CONSTRUCTOR
    // ═══════════════════════════════════════════════════════════════

    constructor(sahne: FikoSahne, modelId?: string) {
        this.sahne = sahne;

        // Initialize PMI Table
        this.pmiTable = {
            modelId: modelId || `model_${Date.now()}`,
            elements: [],
            savedViews: [],
            metadata: {
                standard: 'ASME',
                version: '2.0.0',
                createdAt: new Date()
            }
        };
    }

    // ═══════════════════════════════════════════════════════════════
    // FEATURE CONTROL FRAME OPERATIONS
    // ═══════════════════════════════════════════════════════════════

    /**
     * Yeni FCF ekle
     */
    addFCF(config: FeatureControlFrameConfig): FeatureControlFrame {
        const fcf = new FeatureControlFrame(this.sahne, config);
        const id = fcf.uuid;

        // Add to map
        this.fcfMap.set(id, fcf);

        // Add to scene
        this.sahne.add(fcf);

        // Add to PMI table
        this.addToPMITable({
            id,
            type: PMIElementType.FEATURE_CONTROL_FRAME,
            name: config.name || `FCF_${id}`,
            position: config.position.clone(),
            fcfData: config
        });

        // Validate if enabled
        if (this.autoValidate) {
            const validation = fcf.validate();
            if (!validation.valid) {
                this.emit('validation:error', { fcfId: id, errors: validation.errors });
            }
        }

        // Emit event
        this.emit('fcf:added', { id, fcf });

        return fcf;
    }

    /**
     * Hızlı FCF oluştur
     */
    createQuickFCF(
        position: THREE.Vector3,
        characteristic: GDTCharacteristicType,
        toleranceValue: number,
        options?: {
            zoneModifier?: ToleranceZoneModifier;
            materialCondition?: MaterialCondition;
            datums?: [string?, string?, string?];
            targetPoint?: THREE.Vector3;
        }
    ): FeatureControlFrame {
        const fcf = createFCF(
            this.sahne,
            position,
            characteristic,
            toleranceValue,
            { ...options, style: this.defaultStyle }
        );

        return this.addFCF(fcf.serialize());
    }

    /**
     * FCF güncelle
     */
    updateFCF(id: string, updates: Partial<FeatureControlFrameConfig>): boolean {
        const fcf = this.fcfMap.get(id);
        if (!fcf) return false;

        fcf.updateConfig(updates);

        // Update PMI table
        this.updatePMIElement(id, { fcfData: fcf.serialize() });

        // Validate
        if (this.autoValidate) {
            const validation = fcf.validate();
            if (!validation.valid) {
                this.emit('validation:error', { fcfId: id, errors: validation.errors });
            }
        }

        this.emit('fcf:updated', { id, fcf });
        return true;
    }

    /**
     * FCF sil
     */
    removeFCF(id: string): boolean {
        const fcf = this.fcfMap.get(id);
        if (!fcf) return false;

        // Remove from scene
        this.sahne.remove(fcf);
        fcf.dispose();

        // Remove from map
        this.fcfMap.delete(id);

        // Remove from PMI table
        this.removeFromPMITable(id);

        this.emit('fcf:removed', { id });
        return true;
    }

    /**
     * FCF al
     */
    getFCF(id: string): FeatureControlFrame | undefined {
        return this.fcfMap.get(id);
    }

    /**
     * Tüm FCF'leri al
     */
    getAllFCFs(): FeatureControlFrame[] {
        return Array.from(this.fcfMap.values());
    }

    // ═══════════════════════════════════════════════════════════════
    // DATUM OPERATIONS
    // ═══════════════════════════════════════════════════════════════

    /**
     * Yeni Datum ekle
     */
    addDatum(
        position: THREE.Vector3,
        letter: string,
        options?: {
            type?: DatumType;
            geometryId?: string;
            surfaceType?: 'PLANE' | 'CYLINDER' | 'CONE' | 'SPHERE' | 'COMPLEX';
            normal?: THREE.Vector3;
            description?: string;
        }
    ): DatumFeature {
        // Check if letter already exists
        const existingDatum = this.getDatumByLetter(letter);
        if (existingDatum) {
            throw new Error(`Datum ${letter} already exists`);
        }

        const datum = createDatum(this.sahne, position, letter, options);
        const id = datum.uuid;

        // Add to map
        this.datumMap.set(id, datum);

        // Add to scene
        this.sahne.add(datum);

        // Add to PMI table
        this.addToPMITable({
            id,
            type: PMIElementType.DATUM_FEATURE,
            name: `Datum ${letter}`,
            position: position.clone(),
            datumData: datum.serialize()
        });

        this.emit('datum:added', { id, datum });
        return datum;
    }

    /**
     * Datum Target ekle
     */
    addDatumTarget(
        position: THREE.Vector3,
        datumLetter: string,
        targetNumber: number,
        targetType: 'POINT' | 'LINE' | 'AREA' = 'POINT'
    ): DatumTarget {
        const target = createDatumTarget(
            this.sahne,
            position,
            datumLetter,
            targetNumber,
            targetType
        );

        const id = target.uuid;
        this.datumTargetMap.set(id, target);
        this.sahne.add(target);

        // Add to PMI table
        this.addToPMITable({
            id,
            type: PMIElementType.DATUM_TARGET,
            name: `Datum Target ${datumLetter}${targetNumber}`,
            position: position.clone()
        });

        return target;
    }

    /**
     * Datum güncelle
     */
    updateDatum(id: string, updates: Partial<DatumFeatureInfo>): boolean {
        const datum = this.datumMap.get(id);
        if (!datum) return false;

        datum.updateInfo(updates);
        this.updatePMIElement(id, { datumData: datum.serialize() });

        this.emit('datum:updated', { id, datum });
        return true;
    }

    /**
     * Datum sil
     */
    removeDatum(id: string): boolean {
        const datum = this.datumMap.get(id);
        if (!datum) return false;

        this.sahne.remove(datum);
        datum.dispose();
        this.datumMap.delete(id);
        this.removeFromPMITable(id);

        this.emit('datum:removed', { id });
        return true;
    }

    /**
     * Harfe göre datum bul
     */
    getDatumByLetter(letter: string): DatumFeature | undefined {
        const upperLetter = letter.toUpperCase();
        for (const datum of this.datumMap.values()) {
            if (datum.letter === upperLetter) {
                return datum;
            }
        }
        return undefined;
    }

    /**
     * Tüm datumları al
     */
    getAllDatums(): DatumFeature[] {
        return Array.from(this.datumMap.values());
    }

    /**
     * Kullanılabilir sonraki datum harfini al
     */
    getNextAvailableDatumLetter(): string {
        const usedLetters = new Set(
            Array.from(this.datumMap.values()).map(d => d.letter)
        );

        for (let i = 0; i < 26; i++) {
            const letter = String.fromCharCode(65 + i); // A-Z
            if (!usedLetters.has(letter)) {
                return letter;
            }
        }

        throw new Error('All datum letters are used');
    }

    // ═══════════════════════════════════════════════════════════════
    // VALIDATION
    // ═══════════════════════════════════════════════════════════════

    /**
     * Tüm GD&T'yi doğrula
     */
    validateAll(): GDTValidationResult {
        const errors: GDTValidationError[] = [];
        const warnings: GDTValidationWarning[] = [];

        // Validate all FCFs
        for (const [id, fcf] of this.fcfMap) {
            const result = fcf.validate();
            // result.errors and result.warnings are string arrays
            errors.push(...result.errors.map(e => ({
                code: 'FCF_ERROR',
                message: e,
                elementId: id
            })));
            warnings.push(...result.warnings.map(w => ({
                code: 'FCF_WARNING',
                message: w,
                elementId: id
            })));
        }

        // Check datum references
        for (const [id, fcf] of this.fcfMap) {
            const config = fcf.serialize();
            for (const component of config.components) {
                this.validateDatumReferences(id, component, errors);
            }
        }

        return {
            isValid: errors.length === 0,
            errors,
            warnings
        };
    }

    /**
     * Datum referanslarını doğrula
     */
    private validateDatumReferences(
        fcfId: string,
        component: FCFComponent,
        errors: GDTValidationError[]
    ): void {
        const datums = [
            component.primaryDatum,
            component.secondaryDatum,
            component.tertiaryDatum
        ].filter(Boolean) as DatumReference[];

        for (const datum of datums) {
            const exists = this.getDatumByLetter(datum.letter);
            if (!exists) {
                errors.push({
                    code: 'UNDEFINED_DATUM',
                    message: `Datum ${datum.letter} is referenced but not defined`,
                    elementId: fcfId,
                    suggestion: `Create Datum ${datum.letter} or update the FCF reference`
                });
            }
        }
    }

    /**
     * Auto-validation'ı aç/kapa
     */
    setAutoValidate(enabled: boolean): void {
        this.autoValidate = enabled;
    }

    // ═══════════════════════════════════════════════════════════════
    // PMI TABLE OPERATIONS
    // ═══════════════════════════════════════════════════════════════

    /**
     * PMI tablosuna ekle
     */
    private addToPMITable(element: PMIData): void {
        this.pmiTable.elements.push(element);
        this.emit('pmi:changed', { action: 'add', element });
    }

    /**
     * PMI elemanını güncelle
     */
    private updatePMIElement(id: string, updates: Partial<PMIData>): void {
        const index = this.pmiTable.elements.findIndex(e => e.id === id);
        if (index !== -1) {
            this.pmiTable.elements[index] = {
                ...this.pmiTable.elements[index],
                ...updates,
                updatedAt: new Date()
            };
            this.emit('pmi:changed', { action: 'update', id });
        }
    }

    /**
     * PMI elemanını sil
     */
    private removeFromPMITable(id: string): void {
        const index = this.pmiTable.elements.findIndex(e => e.id === id);
        if (index !== -1) {
            this.pmiTable.elements.splice(index, 1);
            this.emit('pmi:changed', { action: 'remove', id });
        }
    }

    /**
     * PMI tablosunu al
     */
    getPMITable(): PMITable {
        return { ...this.pmiTable };
    }

    /**
     * Kaydedilmiş görünüm ekle
     */
    addSavedView(view: PMISavedView): void {
        this.pmiTable.savedViews.push(view);
    }

    /**
     * Mevcut görünümü kaydet
     */
    saveCurrentView(name: string): PMISavedView {
        const kamera = this.sahne.anaGosterim?.aktifKamera;
        if (!kamera) {
            throw new Error('No active camera');
        }

        const view: PMISavedView = {
            id: `view_${Date.now()}`,
            name,
            cameraPosition: kamera.position.clone(),
            cameraTarget: new THREE.Vector3(0, 0, 0), // Default target
            cameraUp: kamera.up.clone(),
            zoom: kamera.zoom || 1,
            visiblePMIIds: this.pmiTable.elements.map(e => e.id)
        };

        this.addSavedView(view);
        return view;
    }

    // ═══════════════════════════════════════════════════════════════
    // SERIALIZATION
    // ═══════════════════════════════════════════════════════════════

    /**
     * Tümünü JSON'a serialize et
     */
    toJSON(): {
        pmiTable: PMITable;
        fcfs: FeatureControlFrameConfig[];
        datums: DatumFeatureInfo[];
    } {
        return {
            pmiTable: this.pmiTable,
            fcfs: Array.from(this.fcfMap.values()).map(fcf => fcf.serialize()),
            datums: Array.from(this.datumMap.values()).map(d => d.serialize())
        };
    }

    /**
     * JSON'dan yükle
     */
    fromJSON(data: {
        pmiTable?: PMITable;
        fcfs?: FeatureControlFrameConfig[];
        datums?: DatumFeatureInfo[];
    }): void {
        // Clear existing
        this.clear();

        // Load PMI table
        if (data.pmiTable) {
            this.pmiTable = data.pmiTable;
        }

        // Load datums first (FCFs may reference them)
        if (data.datums) {
            for (const datumData of data.datums) {
                const datum = DatumFeature.fromJSON(this.sahne, datumData);
                this.datumMap.set(datum.uuid, datum);
                this.sahne.add(datum);
            }
        }

        // Load FCFs
        if (data.fcfs) {
            for (const fcfData of data.fcfs) {
                const fcf = FeatureControlFrame.fromJSON(this.sahne, fcfData);
                this.fcfMap.set(fcf.uuid, fcf);
                this.sahne.add(fcf);
            }
        }
    }

    // ═══════════════════════════════════════════════════════════════
    // EVENT SYSTEM
    // ═══════════════════════════════════════════════════════════════

    /**
     * Event listener ekle
     */
    on(type: GDTEventType, listener: GDTEventListener): void {
        if (!this.eventListeners.has(type)) {
            this.eventListeners.set(type, new Set());
        }
        this.eventListeners.get(type)!.add(listener);
    }

    /**
     * Event listener kaldır
     */
    off(type: GDTEventType, listener: GDTEventListener): void {
        this.eventListeners.get(type)?.delete(listener);
    }

    /**
     * Event emit et
     */
    private emit(type: GDTEventType, data?: any): void {
        const event: GDTEvent = {
            type,
            data,
            timestamp: Date.now()
        };

        this.eventListeners.get(type)?.forEach(listener => {
            try {
                listener(event);
            } catch (e) {
                console.error('GDT event listener error:', e);
            }
        });
    }

    // ═══════════════════════════════════════════════════════════════
    // UTILITY METHODS
    // ═══════════════════════════════════════════════════════════════

    /**
     * Varsayılan stili ayarla
     */
    setDefaultStyle(style: Partial<FCFStyleConfig>): void {
        this.defaultStyle = style;
    }

    /**
     * Tüm GD&T'yi temizle
     */
    clear(): void {
        // Remove all FCFs
        for (const fcf of this.fcfMap.values()) {
            this.sahne.remove(fcf);
            fcf.dispose();
        }
        this.fcfMap.clear();

        // Remove all datums
        for (const datum of this.datumMap.values()) {
            this.sahne.remove(datum);
            datum.dispose();
        }
        this.datumMap.clear();

        // Remove all datum targets
        for (const target of this.datumTargetMap.values()) {
            this.sahne.remove(target);
            target.dispose();
        }
        this.datumTargetMap.clear();

        // Clear PMI table elements
        this.pmiTable.elements = [];
    }

    /**
     * İstatistikler
     */
    getStatistics(): {
        fcfCount: number;
        datumCount: number;
        datumTargetCount: number;
        pmiElementCount: number;
        savedViewCount: number;
    } {
        return {
            fcfCount: this.fcfMap.size,
            datumCount: this.datumMap.size,
            datumTargetCount: this.datumTargetMap.size,
            pmiElementCount: this.pmiTable.elements.length,
            savedViewCount: this.pmiTable.savedViews.length
        };
    }

    /**
     * Cleanup
     */
    dispose(): void {
        this.clear();
        this.eventListeners.clear();
    }
}

// ====================================================================
// SINGLETON FACTORY
// ====================================================================

const managerInstances = new WeakMap<FikoSahne, GDTManager>();

/**
 * Sahne için GDT Manager al veya oluştur
 */
export function getGDTManager(sahne: FikoSahne, modelId?: string): GDTManager {
    let manager = managerInstances.get(sahne);
    if (!manager) {
        manager = new GDTManager(sahne, modelId);
        managerInstances.set(sahne, manager);
    }
    return manager;
}
